import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CreditCard, Loader2, ShieldCheck, Clock, LogOut } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { BillingIntervalToggle } from '@/components/BillingIntervalToggle';
import { isYearly, yearlyPrice, YEARLY_DISCOUNT_PCT } from '@/lib/billing';
import { useCatalog, PLAN_ORDER } from '@/components/billing/useCatalog';

const firstChargeDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
};

// Full-screen gate for a fresh signup that hasn't added a card yet: $0 today, the picked plan bills on day 8.
export function CardRequiredScreen() {
  const { user, logout } = useAuth();
  const { catalog } = useCatalog();
  const [plan, setPlan] = useState(PLAN_ORDER.includes(user?.signup_plan) ? user.signup_plan : 'starter');
  const [interval, setInterval_] = useState(user?.signup_interval === 'year' ? 'year' : 'month');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (PLAN_ORDER.includes(user?.signup_plan)) setPlan(user.signup_plan); }, [user?.signup_plan]);

  const price = catalog?.prices?.[plan]?.month ?? (catalog?.matrix?.[plan]?.price_cents || 0) / 100;
  const name = catalog?.copy?.plans?.[plan]?.name || plan.charAt(0).toUpperCase() + plan.slice(1);
  const yearly = isYearly(interval);
  const then = yearly ? `$${yearlyPrice(price).toLocaleString()}/yr` : `$${price.toLocaleString()}/mo`;

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post('/billing/checkout', { plan, interval, origin_url: window.location.origin });
      if (r.data?.url) { window.location.href = r.data.url; return; }
      toast.error('Could not open checkout. Please try again.');
      setBusy(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || (typeof d === 'string' ? d : 'Could not open checkout. Please try again.'));
      setBusy(false);
    }
  };

  return (
    <div data-testid="card-required-screen" className="min-h-screen bg-[#07070b] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-2xl">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-transparent p-8 sm:p-10">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-500 flex items-center justify-center mb-6">
            <CreditCard className="h-5 w-5 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-testid="card-required-headline">
            Start your 7-day free trial of {name}
          </h1>
          <p className="mt-3 text-base text-white/55 leading-relaxed" data-testid="card-required-copy">
            Add a card to start — <b className="text-white/85">$0 today</b>. Your {name} plan bills <b className="text-white/85">{then}</b> on{' '}
            <b className="text-white/85">{firstChargeDate()}</b> unless you cancel before then. Cancel in the first 7 days and you&apos;re never charged.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <BillingIntervalToggle value={interval} onChange={setInterval_} />
            {yearly && <span className="text-xs text-emerald-300/90">12 months at {YEARLY_DISCOUNT_PCT}% off, charged once a year after the trial.</span>}
          </div>

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="card-required-plans">
            {PLAN_ORDER.map((id) => {
              const p = catalog?.prices?.[id]?.month ?? (catalog?.matrix?.[id]?.price_cents || 0) / 100;
              const active = id === plan;
              return (
                <button key={id} type="button" data-testid={`card-required-plan-${id}`} onClick={() => setPlan(id)}
                  className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${active ? 'border-violet-500/60 bg-violet-500/15' : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.06]'}`}>
                  <p className={`text-sm font-semibold ${active ? 'text-white' : 'text-white/80'}`}>{catalog?.copy?.plans?.[id]?.name || id}</p>
                  <p className="text-[11px] text-white/45">{yearly ? `$${yearlyPrice(p).toLocaleString()}/yr` : `$${p.toLocaleString()}/mo`}</p>
                </button>
              );
            })}
          </div>

          <button data-testid="card-required-start-btn" onClick={start} disabled={busy || !catalog}
            className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:brightness-110 disabled:opacity-60 text-white font-bold px-6 py-3.5 text-base transition-[filter]">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CreditCard className="h-5 w-5" />}
            Add card &amp; start my free {name} trial — $0 today
          </button>

          <ul className="mt-5 grid sm:grid-cols-3 gap-3 text-xs text-white/50">
            <li className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0" /> Secure checkout by Stripe. We never see your card number.</li>
            <li className="flex items-start gap-2"><Clock className="h-4 w-4 text-violet-300 shrink-0" /> Reminder 3 days before the first charge.</li>
            <li className="flex items-start gap-2"><CreditCard className="h-4 w-4 text-white/40 shrink-0" /> Cancel anytime in Settings → Billing.</li>
          </ul>
        </div>
        <div className="mt-5 flex items-center justify-between text-xs text-white/35">
          <span data-testid="card-required-account">Signed in as {user?.email}</span>
          <button type="button" data-testid="card-required-logout" onClick={logout} className="inline-flex items-center gap-1.5 hover:text-white">
            <LogOut className="h-3.5 w-3.5" /> Log out
          </button>
        </div>
      </div>
    </div>
  );
}
